import { createSlice } from '@reduxjs/toolkit';

const initialFilters = {
  page: 1,
  page_size: 20,
  status: '',
  sort_order: 'desc',
  start_date: null,
  end_date: null,
};

const initialState = {
  items: [],
  pagination: {
    current_page: 1,
    next_page: null,
    total_pages: 0,
    total_size: 0,
    previous_page: null,
    page_size: 20,
  },
  filters: initialFilters,
  selectedOrderId: null,
  loading: false,
  error: null,
  searchQuery: '',
  searchMode: false,
  websocketStatus: 'disconnected',
};

function upsertByOrderId(items, nextItem, toTop = false) {
  const index = items.findIndex((item) => item.order_id === nextItem.order_id);

  if (index === -1) {
    return toTop ? [nextItem, ...items] : [...items, nextItem];
  }

  const merged = { ...items[index], ...nextItem };
  const nextItems = [...items];
  nextItems.splice(index, 1);

  return toTop ? [merged, ...nextItems] : [...nextItems.slice(0, index), merged, ...nextItems.slice(index)];
}

const slice = createSlice({
  name: 'cardRedemption',
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = initialFilters;
    },
    fetchOrdersStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchOrdersSuccess(state, action) {
      state.loading = false;
      state.error = null;
      state.items = action.payload.data || [];
      state.pagination = {
        current_page: action.payload.current_page || 1,
        next_page: action.payload.next_page || null,
        total_pages: action.payload.total_pages || 0,
        total_size: action.payload.total_size || 0,
        previous_page: action.payload.previous_page || null,
        page_size: action.payload.page_size || state.filters.page_size,
      };

      if (
        state.selectedOrderId &&
        !state.items.some((item) => item.order_id === state.selectedOrderId)
      ) {
        state.selectedOrderId = null;
      }
    },
    fetchOrdersFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    setSelectedOrderId(state, action) {
      state.selectedOrderId = action.payload;
    },
    setSearchMode(state, action) {
      state.searchMode = action.payload.searchMode;
      state.searchQuery = action.payload.query || '';
    },
    clearSearchMode(state) {
      state.searchMode = false;
      state.searchQuery = '';
    },
    updateOrder(state, action) {
      state.items = upsertByOrderId(state.items, action.payload);
    },
    prependOrder(state, action) {
      state.items = upsertByOrderId(state.items, action.payload, true);
    },
    removeOrder(state, action) {
      state.items = state.items.filter((item) => item.order_id !== action.payload);
      if (state.selectedOrderId === action.payload) {
        state.selectedOrderId = state.items[0]?.order_id || null;
      }
    },
    setWebsocketStatus(state, action) {
      state.websocketStatus = action.payload;
    },
  },
});

export const {
  setFilters,
  resetFilters,
  fetchOrdersStart,
  fetchOrdersSuccess,
  fetchOrdersFailure,
  setSelectedOrderId,
  setSearchMode,
  clearSearchMode,
  updateOrder,
  prependOrder,
  removeOrder,
  setWebsocketStatus,
} = slice.actions;

export default slice.reducer;
